import { FormControl, FormGroup } from '@angular/forms';
import { optionalNoEmojiValidator } from '../../../shared/validators/emoji.validators';
import {
  isDeclaredValueRequiredDescription,
  optionalDeclaredValueValidator,
  parseDeclaredValue,
  requiredPackageDescriptionWhenDeclaredValueValidator,
} from '../validators/declared-value.validators';

/** Form value for shipment declared value / package description coverage. */
export interface AdditionalCoverageFormValue {
  declaredValue: string;
  packageDescription: string;
}

/** Persisted coverage payload for the Service step. */
export interface AdditionalCoverageSavePayload {
  declaredValue: number | null;
  packageDescription: string | null;
}

/**
 * Creates the Additional Coverage reactive form with linked declared-value validators.
 */
export function createAdditionalCoverageForm(
  initial?: Partial<AdditionalCoverageFormValue>,
): FormGroup<{
  declaredValue: FormControl<string>;
  packageDescription: FormControl<string>;
}> {
  const declaredValue = new FormControl(initial?.declaredValue ?? '', {
    nonNullable: true,
    validators: [optionalDeclaredValueValidator],
  });

  const packageDescription = new FormControl(initial?.packageDescription ?? '', {
    nonNullable: true,
    validators: [
      requiredPackageDescriptionWhenDeclaredValueValidator(declaredValue),
      optionalNoEmojiValidator,
    ],
  });

  declaredValue.valueChanges.subscribe(() => {
    packageDescription.updateValueAndValidity({ emitEvent: false });
  });

  return new FormGroup({ declaredValue, packageDescription });
}

/**
 * Returns whether the Additional Coverage form is currently valid.
 */
export function isAdditionalCoverageValid(
  form: FormGroup<{
    declaredValue: FormControl<string>;
    packageDescription: FormControl<string>;
  }>,
): boolean {
  return form.valid;
}

/**
 * Builds the persistable coverage payload from the form.
 */
export function buildAdditionalCoverageSavePayload(
  form: FormGroup<{
    declaredValue: FormControl<string>;
    packageDescription: FormControl<string>;
  }>,
): AdditionalCoverageSavePayload {
  const parsed = parseDeclaredValue(form.controls.declaredValue.value);
  const declaredValue = parsed !== null && !Number.isNaN(parsed) && parsed > 0 ? parsed : null;
  const description = form.controls.packageDescription.value.trim();

  return {
    declaredValue,
    packageDescription: declaredValue !== null ? description || null : null,
  };
}

/**
 * Returns whether the package description field should show the required indicator.
 */
export function isPackageDescriptionRequired(declaredValue: string): boolean {
  return isDeclaredValueRequiredDescription(declaredValue);
}
